import { Component, Input, Output, EventEmitter } from '@angular/core';
import { ApprovalPoliciesService, ApprovalGroup } from 'src/app/services/approval-policies.service';
import * as alertifyjs from 'alertifyjs';

@Component({
  selector: 'app-group-list',
  templateUrl: './group-list.component.html',
  styleUrls: ['./group-list.component.css']
})
export class GroupListComponent {
  @Input() approvalGroups: ApprovalGroup[] = [];
  @Input() availableWorkConditions: string[] = [];
  @Output() policiesUpdated = new EventEmitter<void>();

  // Estado del componente
  creatingGroup = false;
  expandedGroup: string | null = null;
  editingGroupId: string | null = null;
  editingGroupIdAge: string | null = null;  // NUEVO: Para editar edad
  selectedConditionToAdd: string | null = null;

  // Formularios
  newGroupForm = {
    name: '',
    minIncome: 250,
    maxIncome: 10000,
    minAge: 26,  // NUEVO: Edad mínima por defecto
    maxAge: 70   // NUEVO: Edad máxima por defecto
  };

  editIncomeLimitsForm: { [groupId: string]: { minIncome: number; maxIncome: number } } = {};
  editAgeLimitsForm: { [groupId: string]: { minAge: number; maxAge: number } } = {};  // NUEVO: Para editar edad

  constructor(private approvalPoliciesService: ApprovalPoliciesService) {}

  /**
   * Expandir/colapsar grupo
   */
  toggleGroup(groupName: string): void {
    this.expandedGroup = this.expandedGroup === groupName ? null : groupName;
  }

  /**
   * Iniciar creación de nuevo grupo
   */
  startCreatingGroup(): void {
    this.creatingGroup = true;
  }

  /**
   * Cancelar creación de grupo
   */
  cancelCreatingGroup(): void {
    this.creatingGroup = false;
    this.resetNewGroupForm();
  }

  /**
   * Crear nuevo grupo
   */
  async createGroup(): Promise<void> {
    if (!this.isNewGroupFormValid()) {
      alertifyjs.error('Por favor complete todos los campos requeridos');
      return;
    }

    try {
      await this.approvalPoliciesService.createApprovalGroup(this.newGroupForm);
      alertifyjs.success('Grupo creado exitosamente');
      this.cancelCreatingGroup();
      this.policiesUpdated.emit();
    } catch (error: any) {
      console.error('Error creando grupo:', error);
      alertifyjs.error(error.error?.message || 'Error al crear grupo');
    }
  }

  /**
   * Validar formulario de nuevo grupo
   */
  isNewGroupFormValid(): boolean {
    return this.newGroupForm.name.trim() !== '' &&
           this.newGroupForm.minIncome > 0 &&
           this.newGroupForm.maxIncome > 0 &&
           this.newGroupForm.minIncome < this.newGroupForm.maxIncome &&
           this.newGroupForm.minAge >= 15 &&  // NUEVO
           this.newGroupForm.maxAge <= 100 && // NUEVO
           this.newGroupForm.minAge < this.newGroupForm.maxAge;  // NUEVO
  }

  /**
   * Resetear formulario de nuevo grupo
   */
  resetNewGroupForm(): void {
    this.newGroupForm = {
      name: '',
      minIncome: 250,
      maxIncome: 10000,
      minAge: 26,  // NUEVO
      maxAge: 70   // NUEVO
    };
  }

  /**
   * Obtener color del estado del grupo
   */
  getGroupStatusColor(isActive: boolean): string {
    return isActive ? '#28a745' : '#6c757d';
  }

  /**
   * Formatear ingresos para display
   */
  formatIncome(amount: number): string {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  }

  /**
   * Obtener clase CSS para el estado del grupo
   */
  getGroupStatusClass(isActive: boolean): string {
    return isActive ? 'active' : 'inactive';
  }

  /**
   * Verificar si un grupo está expandido
   */
  isGroupExpanded(groupName: string): boolean {
    return this.expandedGroup === groupName;
  }

  /**
   * Iniciar edición de límites de ingresos
   */
  startEditingIncomeLimits(group: ApprovalGroup): void {
    this.editingGroupId = group.name;
    this.editIncomeLimitsForm[group.name] = {
      minIncome: group.minIncome,
      maxIncome: group.maxIncome
    };
  }

  /**
   * Cancelar edición de límites de ingresos
   */
  cancelEditingIncomeLimits(groupName: string): void {
    this.editingGroupId = null;
    delete this.editIncomeLimitsForm[groupName];
  }

  /**
   * Guardar límites de ingresos editados
   */
  async saveIncomeLimits(groupName: string): Promise<void> {
    const formData = this.editIncomeLimitsForm[groupName];

    if (!formData || formData.minIncome >= formData.maxIncome) {
      alertifyjs.error('El ingreso mínimo debe ser menor que el máximo');
      return;
    }

    try {
      // Usar el nombre del grupo
      await this.approvalPoliciesService.updateApprovalGroupIncomeLimits({
        name: groupName,
        minIncome: formData.minIncome,
        maxIncome: formData.maxIncome
      });

      alertifyjs.success('Límites de ingresos actualizados exitosamente');
      this.editingGroupId = null;
      delete this.editIncomeLimitsForm[groupName];
      this.policiesUpdated.emit();

    } catch (error: any) {
      console.error('Error actualizando límites de ingresos:', error);
      alertifyjs.error(error.error?.message || 'Error al actualizar límites de ingresos');
    }
  }

  /**
   * =====================================================
   * NUEVOS MÉTODOS: Edición de Límites de Edad
   * =====================================================
   */

  /**
   * Verificar si se está editando la edad de un grupo
   */
  isEditingGroupAge(groupName: string): boolean {
    return this.editingGroupIdAge === groupName;
  }

  /**
   * Iniciar edición de límites de edad
   */
  startEditingAgeLimits(group: ApprovalGroup): void {
    this.editingGroupIdAge = group.name;
    this.editAgeLimitsForm[group.name] = {
      minAge: group.minAge || 26,
      maxAge: group.maxAge || 70
    };
  }

  /**
   * Cancelar edición de límites de edad
   */
  cancelEditingAgeLimits(groupName: string): void {
    this.editingGroupIdAge = null;
    delete this.editAgeLimitsForm[groupName];
  }

  /**
   * Guardar límites de edad editados
   */
  async saveAgeLimits(groupName: string): Promise<void> {
    const formData = this.editAgeLimitsForm[groupName];

    if (!formData || formData.minAge >= formData.maxAge) {
      alertifyjs.error('La edad mínima debe ser menor que la máxima');
      return;
    }

    if (formData.minAge < 15 || formData.maxAge > 100) {
      alertifyjs.error('Las edades deben estar entre 15 y 100 años');
      return;
    }

    try {
      await this.approvalPoliciesService.updateApprovalGroupAgeLimits({
        name: groupName,
        minAge: formData.minAge,
        maxAge: formData.maxAge
      });

      alertifyjs.success('Límites de edad actualizados exitosamente');
      this.editingGroupIdAge = null;
      delete this.editAgeLimitsForm[groupName];
      this.policiesUpdated.emit();

    } catch (error: any) {
      console.error('Error actualizando límites de edad:', error);
      alertifyjs.error(error.error?.message || 'Error al actualizar límites de edad');
    }
  }

  /**
   * Agregar condición laboral al grupo
   */
  async addConditionToGroup(group: ApprovalGroup): Promise<void> {
    if (!this.selectedConditionToAdd) {
      alertifyjs.error('Por favor seleccione una condición laboral');
      return;
    }

    try {
      await this.approvalPoliciesService.addWorkConditionToGroup({
        name: group.name,
        workCondition: this.selectedConditionToAdd
      });

      alertifyjs.success(`Condición "${this.selectedConditionToAdd}" agregada al grupo`);
      this.selectedConditionToAdd = null;
      this.policiesUpdated.emit();

    } catch (error: any) {
      console.error('Error agregando condición al grupo:', error);
      alertifyjs.error(error.error?.message || 'Error al agregar condición al grupo');
    }
  }

  /**
   * Remover condición laboral del grupo
   */
  async removeConditionFromGroup(groupName: string, condition: string): Promise<void> {
    if (!confirm(`¿Está seguro de remover la condición "${condition}" del grupo?`)) {
      return;
    }

    try {
      await this.approvalPoliciesService.removeWorkConditionFromGroup({
        name: groupName,
        workCondition: condition
      });

      alertifyjs.success(`Condición "${condition}" removida del grupo`);
      this.policiesUpdated.emit();

    } catch (error: any) {
      console.error('Error removiendo condición del grupo:', error);
      alertifyjs.error(error.error?.message || 'Error al remover condición del grupo');
    }
  }

  /**
   * Eliminar grupo completo
   */
  async deleteGroup(group: ApprovalGroup): Promise<void> {
    if (group.workingConditions.length > 0) {
      alertifyjs.error('No se puede eliminar el grupo porque tiene condiciones laborales asignadas. Elimine las condiciones primero.');
      return;
    }

    if (!confirm(`¿Está seguro de eliminar el grupo "${group.name}"?`)) {
      return;
    }

    try {
      // Usar el nombre del grupo como groupId
      await this.approvalPoliciesService.deleteApprovalGroup(group.name);
      alertifyjs.success(`Grupo "${group.name}" eliminado exitosamente`);
      this.policiesUpdated.emit();

    } catch (error: any) {
      console.error('Error eliminando grupo:', error);
      alertifyjs.error(error.error?.message || 'Error al eliminar grupo');
    }
  }

  /**
   * Verificar si se está editando un grupo específico
   */
  isEditingGroup(groupName: string): boolean {
    return this.editingGroupId === groupName;
  }

  /**
   * Obtener formulario de edición para un grupo
   */
  getEditForm(groupName: string): { minIncome: number; maxIncome: number } | null {
    return this.editIncomeLimitsForm[groupName] || null;
  }
}
